import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { PROFILE_PHOTOS } from '../data/profilePhotos';

const TeamPhotosContext = createContext(null);

export function TeamPhotosProvider({ children }) {
  const [photoBySlug, setPhotoBySlug] = useState(/** @type {Record<string, string>} */ ({}));
  const [status, setStatus] = useState(/** @type {'idle' | 'loading' | 'ready' | 'error'} */ ('idle'));
  const [error, setError] = useState(/** @type {string | null} */ (null));

  const refresh = useCallback(async () => {
    setStatus('loading');
    setError(null);
    const { fetchTeamPhotosMap } = await import('../lib/teamMembersApi');
    const r = await fetchTeamPhotosMap();
    if (r.source !== 'supabase') {
      setPhotoBySlug({});
      setStatus('ready');
      return;
    }
    if (r.error) {
      setPhotoBySlug({});
      setStatus('error');
      setError(r.error);
      return;
    }
    setPhotoBySlug(r.photoBySlug ?? {});
    setStatus('ready');
  }, []);

  useEffect(() => {
    const run = () => refresh();
    if ('requestIdleCallback' in window) {
      const id = requestIdleCallback(run, { timeout: 2500 });
      return () => cancelIdleCallback(id);
    }
    const t = setTimeout(run, 200);
    return () => clearTimeout(t);
  }, [refresh]);

  const photoFor = useCallback(
    (slug) => {
      if (!slug) return undefined;
      const fromApi = photoBySlug[slug];
      if (fromApi) return fromApi;
      const local = PROFILE_PHOTOS[slug];
      return local || undefined;
    },
    [photoBySlug]
  );

  const value = useMemo(
    () => ({
      photoBySlug,
      status,
      error,
      photoFor,
      refresh,
    }),
    [photoBySlug, status, error, photoFor, refresh]
  );

  return <TeamPhotosContext.Provider value={value}>{children}</TeamPhotosContext.Provider>;
}

export function useTeamPhotos() {
  const ctx = useContext(TeamPhotosContext);
  if (!ctx) {
    throw new Error('useTeamPhotos must be used within TeamPhotosProvider');
  }
  return ctx;
}
